import React, { useCallback, useState } from "react";
import type { VisualSuggestionsResult } from "../components/modals/VisualSuggestionsModal";
import postsService from "../services/postsService";
import {
  showError,
  showSuccess,
  showToastError,
  showToastSuccess,
  showWarning,
} from "../shared/alertService";
import apiService, { API_ENDPOINTS } from "../shared/apiService";
import type { UsePostsModalsReturn } from "./usePostsModals";

interface UsePostsAIProps {
  loadPosts: (forceRefresh?: boolean) => Promise<void>;
  stats?: any;
  modals: UsePostsModalsReturn;
  setCustomPrompt: (prompt: string) => void;
  setImageRestrictions: (restrictions: string) => void;
  setImageProvider: (provider: string) => void;
  setImageLogoFile: (file: File | null) => void;
  setImageLogoPreview: (preview: string | null) => void;
  setImageReferenceImages: (images: File[]) => void;
  setImageReferenceImagePreviews: (previews: string[]) => void;
  setShowVisualSuggestions: React.Dispatch<
    React.SetStateAction<Record<number, VisualSuggestionsResult>>
  >;
  setVisualSuggestionsModalPostId: (postId: number | null) => void;
}

export interface UsePostsAIReturn {
  enhancePost: (postId: number) => Promise<void>;
  generateImage: (postId: number, postHasContent?: boolean) => void;
  openEnhanceImageModal: (postId: number, imageUrl: string) => void;
  handleConfirmEnhanceImage: (
    postId: number,
    restrictions?: string,
    provider?: string,
  ) => Promise<void>;
  openVisualSuggestionsModal: (postId: number) => void;
  getVisualSuggestions: (postId: number, model?: string) => Promise<void>;
  checkGrammar: (postId: number) => Promise<void>;
  enhancingImage: number | null;
  checkingGrammar: number | null;
  generatingVisualSuggestions: boolean;
}

export function usePostsAI({
  loadPosts,
  stats,
  modals,
  setCustomPrompt,
  setImageRestrictions,
  setImageProvider,
  setImageLogoFile,
  setImageLogoPreview,
  setImageReferenceImages,
  setImageReferenceImagePreviews,
  setShowVisualSuggestions,
  setVisualSuggestionsModalPostId,
}: UsePostsAIProps): UsePostsAIReturn {
  const [enhancingImage, setEnhancingImage] = useState<number | null>(null);
  const [checkingGrammar, setCheckingGrammar] = useState<number | null>(null);
  const [generatingVisualSuggestions, setGeneratingVisualSuggestions] =
    useState(false);

  const enhancePost = useCallback(
    async (postId: number) => {
      if (!stats?.agents_available) {
        showToastError(
          "Agentes não estão disponíveis. Configure as API keys necessárias.",
        );
        return;
      }

      try {
        modals.setEnhancingPost(postId);
        const result = await postsService.enhancePost(postId);

        const post = result?.data?.post || result?.post;
        const improvements =
          result?.data?.improvements ||
          result?.data?.enhancement?.improvements ||
          result?.improvements ||
          [];

        let message = `✨ Post melhorado com IA!\n\n`;
        message += `📝 Melhorias aplicadas: ${improvements.join(", ") || "Post otimizado"}\n\n`;

        if (post.hashtags) {
          let hashtagsArray: string[] = [];
          if (Array.isArray(post.hashtags)) {
            hashtagsArray = post.hashtags as string[];
          } else if (
            post.hashtags &&
            typeof post.hashtags === "string" &&
            (post.hashtags as string).trim()
          ) {
            hashtagsArray = (post.hashtags as string)
              .split(",")
              .map((tag: string) => tag.trim())
              .filter((tag: string) => tag.length > 0);
          }
          if (hashtagsArray.length > 0) {
            message += `🏷️ Hashtags adicionadas: ${hashtagsArray.join(" ")}\n\n`;
          }
        }
        if (post.call_to_action) {
          message += `📢 Call to Action: ${post.call_to_action}\n\n`;
        }

        message += `🎯 O post foi otimizado seguindo as melhores práticas de copywriting e engagement!`;

        showSuccess(message, "Post melhorado");
        await loadPosts();
        showToastSuccess("Post melhorado com IA!");
      } catch (error: any) {
        console.error("Erro ao melhorar post:", error);

        let errorMessage = "Erro ao melhorar post.";
        if (error?.statusCode === 500) {
          errorMessage =
            "Erro no servidor ao melhorar post. Verifique se o post tem conteúdo e tente novamente.";
        } else if (error?.statusCode === 400) {
          errorMessage = "Post não possui conteúdo para melhorar.";
        } else if (error?.message) {
          errorMessage = error.message;
        }

        showError(errorMessage, "Erro ao melhorar post");
        showToastError(errorMessage);
      } finally {
        modals.setEnhancingPost(null);
      }
    },
    [stats, modals, loadPosts],
  );

  const generateImage = useCallback(
    (postId: number, postHasContent?: boolean) => {
      modals.setImageModalMode("generate");
      modals.setImageModalPostHasContent(!!postHasContent);
      modals.setShowImageModal(postId);
      setCustomPrompt("");
      setImageRestrictions("");
      setImageProvider("dalle");
      setImageLogoFile(null);
      setImageLogoPreview(null);
      setImageReferenceImages([]);
      setImageReferenceImagePreviews([]);
    },
    [
      modals,
      setCustomPrompt,
      setImageRestrictions,
      setImageProvider,
      setImageLogoFile,
      setImageLogoPreview,
      setImageReferenceImages,
      setImageReferenceImagePreviews,
    ],
  );

  const openEnhanceImageModal = useCallback(
    (postId: number, imageUrl: string) => {
      modals.setShowEnhanceImageModal(postId);
      modals.setEnhanceImageImageUrl(imageUrl);
    },
    [modals],
  );

  const handleConfirmEnhanceImage = useCallback(
    async (postId: number, restrictions?: string, provider?: string) => {
      if (!stats?.agents_available) {
        showToastError(
          "Agentes não estão disponíveis. Configure as API keys necessárias.",
        );
        return;
      }

      try {
        setEnhancingImage(postId);
        const body: { restrictions?: string; provider?: string } = {};
        if (restrictions?.trim()) body.restrictions = restrictions.trim();
        if (provider?.trim()) body.provider = provider.trim().toLowerCase();

        const result = await postsService.enhanceImageAndWait(postId, body);
        const imageInfo =
          (result as any)?.image_info || (result as any)?.data?.image_info;
        const filename = imageInfo?.filename || "arquivo gerado";
        showToastSuccess(
          `Imagem melhorada com análise inteligente! Arquivo: ${filename}`,
        );
        modals.setShowEnhanceImageModal(null);
        modals.setEnhanceImageImageUrl("");
        await loadPosts(true);
      } catch (error: any) {
        console.error("Erro ao melhorar imagem:", error);
        if (
          error?.statusCode === 402 ||
          error?.errorData?.code === "insufficient_credits"
        ) {
          // Aviso de créditos já exibido pelo apiService
          return;
        }
        let errorMessage = "Erro ao melhorar imagem. Tente novamente.";
        if (error?.statusCode === 404) {
          errorMessage =
            "Funcionalidade de melhoria de imagem não disponível no momento.";
        } else if (error?.statusCode === 500) {
          errorMessage =
            "Erro no servidor ao melhorar imagem. Verifique se o post tem imagem e se as API keys estão configuradas.";
        } else if (error?.statusCode === 400) {
          errorMessage =
            error?.message || "Post não possui imagem para melhorar.";
        } else if (error?.message) {
          errorMessage = error.message;
        }
        showToastError(errorMessage);
      } finally {
        setEnhancingImage(null);
      }
    },
    [stats, loadPosts, modals],
  );

  const openVisualSuggestionsModal = useCallback(
    (postId: number) => {
      setVisualSuggestionsModalPostId(postId);
    },
    [setVisualSuggestionsModalPostId],
  );

  const getVisualSuggestions = useCallback(
    async (postId: number, model?: string) => {
      if (!stats?.agents_available) {
        showWarning(
          "Agentes não estão disponíveis. Configure as API keys necessárias.",
          "Agentes indisponíveis",
        );
        return;
      }

      try {
        setGeneratingVisualSuggestions(true);
        const url = API_ENDPOINTS.POSTS_EXTENDED.VISUAL_SUGGESTIONS.replace(
          "{id}",
          String(postId),
        );
        const params = model ? { model } : undefined;
        const result = await apiService.get(url, params);
        setShowVisualSuggestions((prev) => ({
          ...prev,
          [postId]: result as VisualSuggestionsResult,
        }));
        showSuccess(
          "Sugestões visuais obtidas com sucesso.",
          "Sugestões visuais",
        );
      } catch (error: any) {
        console.error("Erro ao obter sugestões visuais:", error);
        const errorMessage =
          error?.statusCode === 404
            ? "Funcionalidade de sugestões visuais não disponível no momento."
            : error?.statusCode === 500
              ? "Erro no servidor ao obter sugestões. Verifique se o post tem conteúdo."
              : error?.message || "Erro ao obter sugestões. Tente novamente.";
        showError(errorMessage, "Erro ao obter sugestões");
        showToastError(errorMessage);
      } finally {
        setGeneratingVisualSuggestions(false);
      }
    },
    [stats, setShowVisualSuggestions],
  );

  const checkGrammar = useCallback(
    async (postId: number) => {
      if (!stats?.agents_available) {
        showToastError(
          "Agentes não estão disponíveis. Configure as API keys necessárias.",
        );
        return;
      }

      try {
        setCheckingGrammar(postId);
        const result = await apiService.post(
          API_ENDPOINTS.POSTS_EXTENDED.CHECK_GRAMMAR.replace(
            "{id}",
            String(postId),
          ),
        );

        const corrections =
          result?.corrections || result?.data?.corrections || [];

        let toastMessage = "Gramática verificada com sucesso!";
        if (corrections.length > 0) {
          toastMessage = `Gramática corrigida! ${corrections.length} correção(ões) aplicada(s).`;
        }
        showToastSuccess(toastMessage);
        await loadPosts(true);
      } catch (error: any) {
        console.error("Erro ao verificar gramática:", error);
        let errorMessage = "Erro ao verificar gramática. Tente novamente.";
        if (error?.statusCode === 500) {
          errorMessage =
            "Erro no servidor ao verificar gramática. Verifique se o post tem conteúdo.";
        } else if (error?.statusCode === 400) {
          errorMessage =
            error?.message ||
            "Post não possui conteúdo para verificar gramática.";
        } else if (error?.statusCode === 404) {
          errorMessage =
            "Funcionalidade de verificação gramatical não disponível no momento.";
        } else if (error?.message) {
          errorMessage = error.message;
        }
        showToastError(errorMessage);
      } finally {
        setCheckingGrammar(null);
      }
    },
    [stats, loadPosts],
  );

  return {
    enhancePost,
    generateImage,
    openEnhanceImageModal,
    handleConfirmEnhanceImage,
    openVisualSuggestionsModal,
    getVisualSuggestions,
    checkGrammar,
    enhancingImage,
    checkingGrammar,
    generatingVisualSuggestions,
  };
}
